// Thin browser-level network watcher.
//
// Phase 2 (full redesign) — all state moved to Vuex `connectionHealth`:
//   - browser online/offline events here are the ONLY input this module owns
//   - listener errors are reported by `libs/firestoreListener.js`
//   - banner / popup state lives in `store/modules/connectionHealth.js`
//
// No more active probing. The legacy heartbeat / recovery-poll loop that
// race-condition'd against onSnapshot listeners on data-heavy pages is gone.
// `checkFirestoreConnection` is preserved as an exported helper because the
// banner's manual "Retry" button and the catastrophic `/connection-error`
// page still need an on-demand probe.

import fc from "@/firebaseConfig";

const PROBE_TIMEOUT_MS = 12000;
const OFFLINE_GRACE_MS = 2000;

const CONNECTION_ERROR_CODES = [
  "unavailable",
  "deadline-exceeded",
  "cancelled",
  "internal",
];

function isConnectionError(err) {
  if (!err) return false;
  if (err.__timeout) return true;
  return CONNECTION_ERROR_CODES.includes(err.code);
}

// One-shot server reachability check. Used by the banner's manual Retry
// button and the catastrophic-fallback page. Not used in a loop.
export async function checkFirestoreConnection() {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
  const user = fc.auth.currentUser;
  if (!user) return true;

  let timer;
  const timeout = new Promise((resolve, reject) => {
    timer = setTimeout(() => reject({ __timeout: true }), PROBE_TIMEOUT_MS);
  });
  try {
    await Promise.race([
      fc.users.doc(user.uid).get({ source: "server" }),
      timeout,
    ]);
    return true;
  } catch (err) {
    return !isConnectionError(err);
  } finally {
    clearTimeout(timer);
  }
}

let _store = null;
function getStore() {
  if (_store) return _store;
  // eslint-disable-next-line global-require
  _store = require("@/store").default;
  return _store;
}

let offlineGraceTimer = null;
let listenersBound = false;
let handlers = {};

function clearOfflineGrace() {
  if (offlineGraceTimer) {
    clearTimeout(offlineGraceTimer);
    offlineGraceTimer = null;
  }
}

function handleBrowserOffline() {
  if (offlineGraceTimer) return;
  offlineGraceTimer = setTimeout(() => {
    offlineGraceTimer = null;
    const store = getStore();
    if (store) store.dispatch("connectionHealth/setNetworkOffline");
    if (handlers.onLost) handlers.onLost("network");
  }, OFFLINE_GRACE_MS);
}

function handleBrowserOnline() {
  clearOfflineGrace();
  const store = getStore();
  if (store) store.dispatch("connectionHealth/setNetworkOnline");
  if (handlers.onRestored) handlers.onRestored();
}

function bindBrowserEvents() {
  if (listenersBound || typeof window === "undefined") return;
  window.addEventListener("offline", handleBrowserOffline);
  window.addEventListener("online", handleBrowserOnline);
  listenersBound = true;
}

function unbindBrowserEvents() {
  if (!listenersBound || typeof window === "undefined") return;
  window.removeEventListener("offline", handleBrowserOffline);
  window.removeEventListener("online", handleBrowserOnline);
  listenersBound = false;
}

// Begin watching network state. Old API preserved (App.vue still calls
// startConnectionMonitor / stopConnectionMonitor) but the only signal
// emitted is `reason='network'`. Firestore backend state is reported by
// `libs/firestoreListener.js` directly to Vuex without going through here.
export function startConnectionMonitor(opts) {
  stopConnectionMonitor();
  handlers = opts || {};
  bindBrowserEvents();

  // Seed initial network state from navigator.onLine.
  const store = getStore();
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    if (store) store.dispatch("connectionHealth/setNetworkOffline");
    if (handlers.onLost) handlers.onLost("network");
  } else if (store) {
    store.dispatch("connectionHealth/setNetworkOnline");
  }

  // Dev console hook so connection-health state can be inspected and
  // forced from the browser without unplugging anything:
  //   window.__connectionHealth.state()
  //   window.__connectionHealth.simulateDegraded()
  //   window.__connectionHealth.simulateDown()
  //   window.__connectionHealth.simulateError(code)
  //   window.__connectionHealth.simulateRestore()
  //   window.__connectionHealth.checkServer() — manual one-shot probe
  if (process.env.NODE_ENV !== "production" && typeof window !== "undefined") {
    window.__connectionHealth = {
      state: () => (store ? store.state.connectionHealth : null),
      simulateDegraded: () => store && store.dispatch("connectionHealth/markBackendDegraded"),
      simulateDown: () => store && store.dispatch("connectionHealth/markBackendDown"),
      simulateError: (code) =>
        store &&
        store.dispatch("connectionHealth/recordListenerError", {
          code: code || "unavailable",
          source: "devtools",
        }),
      simulateRestore: () => store && store.dispatch("connectionHealth/markBackendHealthy"),
      simulateOffline: () => {
        const s = getStore();
        if (s) s.dispatch("connectionHealth/setNetworkOffline");
      },
      simulateOnline: () => {
        const s = getStore();
        if (s) s.dispatch("connectionHealth/setNetworkOnline");
      },
      checkServer: checkFirestoreConnection,
    };
  }
}

export function stopConnectionMonitor() {
  clearOfflineGrace();
  unbindBrowserEvents();
  handlers = {};
}

// On-demand probe + Vuex state flip. Used by the banner's manual Retry
// button and the catastrophic-fallback page.
export async function retryConnection() {
  const ok = await checkFirestoreConnection();
  const store = getStore();
  if (ok && store) {
    store.dispatch("connectionHealth/markBackendHealthy");
  }
  return ok;
}

// Legacy API kept so existing imports don't break; effectively a no-op now
// because nothing in the active codebase still pushes a "lost" event.
export function reportConnectionLost() {
  const store = getStore();
  if (store) store.dispatch("connectionHealth/markBackendDown");
}
